import { useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { toast } from 'react-toastify';

import useElysiaStore from '@/app/elysia-store';
import notify from '../services/notify';
import { getContentSignedUrl, uploadFileUsingUrl, triggerIngestion } from '../services/content';
import { checkForDuplicateFiles, getUniquePastedFileName } from '@/utils/validate';
import {
  ALLOWED_TYPES,
  FILE_COUNT_MISMATCH_ID,
  FILE_PASTE_UNSUPPORTED_ID,
  FILE_UPLOAD_ACCEPTED_ID,
  FILE_UPLOAD_ERROR_ID,
  FILE_UPLOAD_FAIL_ID,
  FILE_UPLOAD_SIZE_ID,
  FILE_UPLOAD_TYPE_ID,
  IMAGE_ALLOWED_TYPES,
  MAX_FILE_COUNT,
  IMAGE_INGESTION_SLOW_ID,
} from '../config/settings';
import { sheetAction, showSheetGuidelines } from '@/utils/showSheetGuidelines ';

interface DisplaySource {
  name: string;
  title: string;
  status: string;
  iconState: 'loading' | 'success' | 'idle';
}

interface DisplaySource {
  name: string; // identity (used for remove, etc.)
  title: string; // what the user sees
  status: string;
  iconState: 'loading' | 'success' | 'idle';
}

const buildDisplaySource = (
  name: string,
  attachedFileMap: Map<string, File>,
  fileUploadStatusMap: Record<string, string>,
  loadingFilesMap: Record<string, boolean>,
  titleMap: Record<string, string>
): DisplaySource => {
  const title = titleMap[name] ?? name;
  const isAttached = attachedFileMap.has(name);

  if (!isAttached) {
    return { name, title, status: 'Added to chat', iconState: 'idle' };
  }

  const status = fileUploadStatusMap[name] ?? '';
  const iconState = loadingFilesMap[name]
    ? 'loading'
    : status === 'successful'
      ? 'success'
      : 'idle';

  return { name, title, status, iconState };
};

export const useAttachedFiles = () => {
  const attachedFileArray = useElysiaStore((state) => state.attachedFileArray);
  const setAttachedFileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const isFileDropped = useElysiaStore((state) => state.isFileDropped);
  const setIsFileDropped = useElysiaStore((state) => state.setIsFileDropped);
  const uploadedFileTitle = useElysiaStore((state) => state.uploadedFileTitle);
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const selectedSources = useElysiaStore((state) => state.selectedSources);
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const sources = useElysiaStore((state) => state.sources);
  const setSources = useElysiaStore.getState().setSources;
  const availablePrivateDocs = useElysiaStore((state) => state.availablePrivateDocs);
  const setAvailablePrivateDocs = useElysiaStore.getState().setAvailablePrivateDocs;
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const showFileToasts = useElysiaStore((state) => state.showFileToasts);
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const dragAndDropFiles = useElysiaStore((state) => state.dragAndDropFiles);
  const setDragAndDropFiles = useElysiaStore.getState().setDragAndDropFiles;
  const chatInputSources = useElysiaStore((state) => state.chatInputSources);
  const setChatInputSources = useElysiaStore.getState().setChatInputSources;
  const chatInputSourceCategories = useElysiaStore((state) => state.chatInputSourceCategories);
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const setChatInputSourceCategories = useElysiaStore.getState().setChatInputSourceCategories;
  const promptToolsConfig = useElysiaStore((state) => state.promptToolsConfig);
  const chatInputSourceTitles = useElysiaStore((state) => state.chatInputSourceTitles);
  const setChatInputSourceTitles = useElysiaStore.getState().setChatInputSourceTitles;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const slowIngestTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const [fileUploadStatusMap, setFileUploadStatusMap] = useState<Record<string, string>>({});
  const [loadingFilesMap, setLoadingFilesMap] = useState<Record<string, boolean>>({});

  const startSlowImageTimer = (file: File) => {
    if (!file.type.startsWith('image/')) return;

    slowIngestTimersRef.current[file.name] = setTimeout(() => {
      notify({
        message:
          'This image is taking longer than usual to be ingested and will be available for prompting shortly.',
        type: 'error',
        toastId: `${IMAGE_INGESTION_SLOW_ID}-${file.name}`,
      });
    }, 15000);
  };

  const clearSlowImageTimer = (fileName: string) => {
    const timer = slowIngestTimersRef.current[fileName];
    if (timer) {
      clearTimeout(timer);
      delete slowIngestTimersRef.current[fileName];
    }
    toast.dismiss(`${IMAGE_INGESTION_SLOW_ID}-${fileName}`);
  };

  useEffect(() => {
    if (showFileToasts === false) {
      toast.dismiss(FILE_UPLOAD_ERROR_ID);
      toast.dismiss(FILE_UPLOAD_ACCEPTED_ID);
      toast.dismiss(FILE_UPLOAD_TYPE_ID);
      toast.dismiss(FILE_UPLOAD_FAIL_ID);
      toast.dismiss(FILE_UPLOAD_SIZE_ID);
    }
  }, [showFileToasts]);

  const orderedNamesRef = useRef<string[]>([]);

  const displaySources: DisplaySource[] = useMemo(() => {
    const attachedNames = attachedFileArray.map((file) => file.name);
    const selectedNames = chatInputSources ?? [];

    const allCurrentNames = new Set([...attachedNames, ...selectedNames]);

    const survivingOrder = orderedNamesRef.current.filter((name) => allCurrentNames.has(name));

    const seen = new Set(survivingOrder);
    const newNames = [...attachedNames, ...selectedNames].filter((name) => {
      if (seen.has(name)) return false;
      seen.add(name);
      return true;
    });

    const chronologicalOrder = [...survivingOrder, ...newNames];
    orderedNamesRef.current = chronologicalOrder;

    const pendingOrder = chronologicalOrder;

    const attachedFileMap = new Map(attachedFileArray.map((file) => [file.name, file]));

    return pendingOrder.map((name) =>
      buildDisplaySource(
        name,
        attachedFileMap,
        fileUploadStatusMap,
        loadingFilesMap,
        chatInputSourceTitles ?? {}
      )
    );
  }, [
    attachedFileArray,
    chatInputSources,
    fileUploadStatusMap,
    loadingFilesMap,
    chatInputSourceTitles,
  ]);

  const isAnyFileUploading = attachedFileArray.some(
    (file) => loadingFilesMap[file.name] || fileUploadStatusMap[file.name] === 'successful'
  );

  const isStillAttached = (name: string) =>
    useElysiaStore.getState().attachedFileArray.some((f) => f.name === name);

  //pulse manager app
  const requiredFileCount =
    typeof promptToolsConfig?.fileRequired === 'number' && promptToolsConfig.fileRequired > 0
      ? promptToolsConfig.fileRequired
      : 0;
  const isFixedFileFlow = requiredFileCount > 0;

  const validateRequiredCount = (count: number) => {
    if (!isFixedFileFlow || count === requiredFileCount) return true;

    notify({
      message: `This app requires only ${requiredFileCount} files: Executive Summary and Comments Report. These can be downloaded from Viva Glint.`,
      type: 'error',
      toastId: FILE_COUNT_MISMATCH_ID,
    });
    return false;
  };

  const uploadFixedFileSet = async (files: File[]) => {
    const titles = files.map((f) => f.name);
    const lastFile = files[files.length - 1];

    const markAll = <T>(value: T) => Object.fromEntries(titles.map((n) => [n, value]));

    setLoadingFilesMap((prev) => ({ ...prev, ...markAll(true) }));
    setFileUploadStatusMap((prev) => ({ ...prev, ...markAll('Analysing file...') }));

    const rollback = () => {
      const notInSet = (s: string) => !titles.includes(s);
      setSources?.((prev = []) => prev.filter(notInSet));
      setSelectedSources?.((prev = []) => prev.filter(notInSet));
      setChatInputSources?.((prev = []) => prev.filter(notInSet));
      setAttachedFileArray?.((prev) => prev.filter((f) => !titles.includes(f.name)));
      setUploadedFile?.(null);
      setUploadedFileTitle?.('');
      if (chatAgent !== 'app-agent' && sources?.length === 0) {
        setChatAgent?.('Generic');
      }
      if (chatAgent === 'app-agent' && sources?.length === 0) {
        setBrandDoc(false);
      }
    };

    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const res = await getContentSignedUrl('pulse-managers-agent', '', file.name, file.name);
          if (!res?.url) throw new Error(`No upload URL returned for ${file.name}`);
          await uploadFileUsingUrl(res.url, file);
          return { name: file.name, path: res.path as string };
        })
      );

      const ingestionRes = await triggerIngestion(
        uploaded[uploaded.length - 1].path,
        lastFile.name,
        false,
        'pulse-managers-agent'
      );
      if (!titles.some(isStillAttached)) return;

      if (ingestionRes?.status === 'success') {
        const addAll = (prev: string[] = []) => Array.from(new Set([...prev, ...titles]));
        setSelectedSources?.(addAll);
        setChatInputSources?.(addAll);

        if (chatAgent === 'app-agent') {
          setBrandDoc(true);
        } else {
          setChatAgent?.('Document');
        }

        setFileUploadStatusMap((prev) => ({ ...prev, ...markAll('successful') }));
        setAvailablePrivateDocs?.([...(availablePrivateDocs || []), ...titles]);

        setTimeout(() => {
          setFileUploadStatusMap((prev) => ({ ...prev, ...markAll('Added to chat') }));
        }, 2000);

        notify({ message: ingestionRes?.message, type: 'success' });
        rollback();
      } else {
        notify({
          message: `${ingestionRes?.message ?? 'Something went wrong'}`,
          type: 'error',
          toastId: `${FILE_UPLOAD_ERROR_ID}`,
        });
        rollback();
      }
    } catch (e: any) {
      console.error(e);
      setFileUploadStatusMap((prev) => ({ ...prev, ...markAll('failed') }));
      notify({
        message: e?.response?.data?.message ?? 'Failed to upload files. Please try again.',
        type: 'error',
        toastId: FILE_UPLOAD_FAIL_ID,
      });
      rollback();
    } finally {
      setLoadingFilesMap((prev) => ({ ...prev, ...markAll(false) }));
      setIsFileDropped(false);
    }
  };

  const uploadFile = async (file: File, title: string) => {
    setLoadingFilesMap((prev) => ({ ...prev, [file.name]: true }));
    setFileUploadStatusMap((prev) => ({ ...prev, [file.name]: 'Analysing file...' }));

    const toastIds = {
      accepted: `${FILE_UPLOAD_ACCEPTED_ID}-${file.name}`,
      error: `${FILE_UPLOAD_ERROR_ID}-${file.name}`,
      fail: `${FILE_UPLOAD_FAIL_ID}-${file.name}`,
    };
    startSlowImageTimer(file);
    try {
      const response = await getContentSignedUrl('my-documents', '', file.name, title || file.name);
      if (response?.url && file) {
        await uploadFileUsingUrl(response.url, file);
        const ingestionRes = await triggerIngestion(
          response.path,
          title || file.name,
          true,
          'my-documents'
        );

        if (!isStillAttached(file.name)) return;
        setLoadingFilesMap((prev) => ({ ...prev, [file.name]: false }));

        if (ingestionRes?.status === 'success') {
          setSources?.((prev = []) => (prev.includes(title) ? prev : [...prev, title]));
          setSelectedSources?.((prev = []) => (prev.includes(title) ? prev : [...prev, title]));
          setChatInputSources?.((prev = []) => (prev.includes(title) ? prev : [...prev, title]));
          setCategoryCount?.((prev) => ({
            ...prev,
            'my-documents': (prev?.['my-documents'] ?? 0) + 1,
          }));

          setChatInputSourceCategories?.((prev) => ({
            ...prev,
            [title]: 'my-documents',
          }));

          if (chatAgent === 'app-agent') {
            setBrandDoc(true);
          } else {
            setChatAgent?.('Document');
          }

          setFileUploadStatusMap((prev) => ({ ...prev, [file.name]: 'successful' }));
          setAvailablePrivateDocs?.([...(availablePrivateDocs || []), file.name]);

          setTimeout(() => {
            setFileUploadStatusMap((prev) => ({ ...prev, [file.name]: 'Added to chat' }));
          }, 2000);

          notify({ message: ingestionRes?.message, type: 'success' });
        } else if (ingestionRes?.status === 'accepted') {
          notify({
            message: `${file.name} - ${ingestionRes?.message}`,
            type: 'error',
            toastId: toastIds.accepted,
          });
          setSources?.((prev = []) => prev.filter((s) => s !== title));
          setSelectedSources?.((prev = []) => prev.filter((s) => s !== title));
          setAttachedFileArray?.((prev) => prev.filter((f) => f.name !== file.name));
          if (uploadedFileTitle === file.name) {
            setUploadedFile?.(null);
            setUploadedFileTitle?.('');
          }
        } else if (ingestionRes?.status === 'error') {
          notify({
            message: `${file.name} - ${ingestionRes?.message ?? 'Something went wrong'}`,
            type: 'error',
            toastId: toastIds.error,
          });
          setSources?.((prev = []) => prev.filter((s) => s !== title));
          setSelectedSources?.((prev = []) => prev.filter((s) => s !== title));
          setAttachedFileArray?.((prev) => prev.filter((f) => f.name !== file.name));
          if (uploadedFileTitle === file.name) {
            setUploadedFile?.(null);
            setUploadedFileTitle?.('');
          }
          if (chatAgent !== 'app-agent' && sources?.length === 0) {
            setChatAgent?.('Generic');
          }
        }
      }
    } catch (e: any) {
      if (!isStillAttached(file.name)) return;
      setFileUploadStatusMap((prev) => ({ ...prev, [file.name]: 'failed' }));
      const displayMessage =
        e?.response?.data?.message ?? 'Failed to upload file. Please try again.';
      const isIngestionError = e?.response?.data?.status === 'error';
      console.error(e);
      notify({
        message: `${file.name} - ${displayMessage}`,
        type: 'error',
        toastId: toastIds.fail,
        ...(isIngestionError ? sheetAction(file) : {}),
      });
      setSources?.((prev = []) => prev.filter((s) => s !== title));
      setSelectedSources?.((prev = []) => prev.filter((s) => s !== title));
      setAttachedFileArray?.((prev) => prev.filter((f) => f.name !== file.name));
      if (uploadedFileTitle === file.name) {
        setUploadedFile?.(null);
        setUploadedFileTitle?.('');
      }
      if (chatAgent !== 'app-agent' && sources?.length === 0) {
        setChatAgent?.('Generic');
      }
    } finally {
      setLoadingFilesMap((prev) => ({ ...prev, [file.name]: false }));
      setIsFileDropped(false);
      clearSlowImageTimer(file.name);
    }
  };

  useEffect(() => {
    const uploadOndrag = async () => {
      if (!isFileDropped || dragAndDropFiles.length === 0) return;

      const filesToUpload = [...dragAndDropFiles];
      setDragAndDropFiles([]);
      setIsFileDropped(false);

      showSheetGuidelines(filesToUpload);

      if (isFixedFileFlow) {
        if (!validateRequiredCount(filesToUpload.length)) {
          // in case the drop zone already pushed them into attachedFileArray
          const dropped = new Set(filesToUpload.map((f) => f.name));
          setAttachedFileArray?.((prev) => prev.filter((f) => !dropped.has(f.name)));
          return;
        }

        setUploadedFile?.(filesToUpload[filesToUpload.length - 1]);
        setUploadedFileTitle?.(filesToUpload[filesToUpload.length - 1]?.name);
        await uploadFixedFileSet(filesToUpload);
        return;
      }

      setUploadedFile?.(filesToUpload[filesToUpload.length - 1]);
      setUploadedFileTitle?.(filesToUpload[filesToUpload.length - 1]?.name);
      await Promise.all(filesToUpload.map((file) => uploadFile(file, file.name)));
    };

    uploadOndrag();
    // eslint-disable-next-line
  }, [isFileDropped]);

  const validateAndUploadFiles = async (candidateFiles: File[], allowedTypes: string[]) => {
    if (isFixedFileFlow) {
      if (!validateRequiredCount(candidateFiles.length)) return;

      if (new Set(candidateFiles.map((f) => f.name)).size !== candidateFiles.length) {
        notify({
          message: 'Please choose files with different names.',
          type: 'error',
          toastId: FILE_COUNT_MISMATCH_ID,
        });
        return;
      }
    } else if (candidateFiles.length > MAX_FILE_COUNT) {
      notify({
        message: `You can only add up to ${MAX_FILE_COUNT} files at a time.`,
        type: 'error',
      });
      return;
    }

    let nonDuplicateFiles = candidateFiles;
    if (!isFixedFileFlow) {
      const duplicates = checkForDuplicateFiles(candidateFiles);
      nonDuplicateFiles = candidateFiles.filter(
        (file) => !duplicates.some((d) => d.name === file.name)
      );
    }
    if (nonDuplicateFiles.length === 0) return;

    toast.dismiss(FILE_UPLOAD_ERROR_ID);
    toast.dismiss(FILE_UPLOAD_ACCEPTED_ID);
    toast.dismiss(FILE_UPLOAD_TYPE_ID);
    toast.dismiss(FILE_UPLOAD_FAIL_ID);
    toast.dismiss(FILE_UPLOAD_SIZE_ID);

    const validFiles: File[] = [];

    nonDuplicateFiles.forEach((file) => {
      const isValidSize = file.size <= 31457280; // 30mb
      const isValidFileType = allowedTypes.includes(file.type);

      if (!isValidSize) {
        notify({
          message: `"${file.name}" exceeds the max allowed size of 30 MB.`,
          type: 'error',
          toastId: `${FILE_UPLOAD_SIZE_ID}-${file.name}`,
        });
        return;
      }

      if (!isValidFileType) {
        setBrandDoc(false);
        notify({
          message: `Elysia does not support the file type of "${file.name}"`,
          type: 'error',
          toastId: `${FILE_UPLOAD_TYPE_ID}-${file.name}`,
        });
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length === 0) return;

    showSheetGuidelines(validFiles);

    if (isFixedFileFlow) {
      if (validFiles.length !== nonDuplicateFiles.length) return;

      const previousNames = attachedFileArray.map((f) => f.name);
      if (previousNames.length > 0) {
        const notPrevious = (s: string) => !previousNames.includes(s);
        setSources?.((prev = []) => prev.filter(notPrevious));
        setSelectedSources?.((prev = []) => prev.filter(notPrevious));
        setChatInputSources?.((prev = []) => prev.filter(notPrevious));
      }

      setAttachedFileArray?.(validFiles);
      setUploadedFile?.(validFiles[validFiles.length - 1]);
      setUploadedFileTitle?.(validFiles[validFiles.length - 1].name);

      await uploadFixedFileSet(validFiles);
      return;
    }

    setAttachedFileArray?.((prev) => [...prev, ...validFiles]);
    setUploadedFile?.(validFiles[validFiles.length - 1]);
    setUploadedFileTitle?.(validFiles[validFiles.length - 1].name);

    await Promise.all(validFiles.map((file) => uploadFile(file, file.name)));
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    setShowFileToasts?.(true);
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const selectedFiles = Array.from(files);
    const allowedTypes = [...ALLOWED_TYPES];

    await validateAndUploadFiles(selectedFiles, allowedTypes);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboard = event.clipboardData;
    const items = clipboard?.items;
    if (!items || items.length === 0) return;

    const hasFileItem = Array.from(items).some((item) => item.kind === 'file');
    if (!hasFileItem) return;

    const html = clipboard.getData('text/html');
    const plain = clipboard.getData('text/plain');

    const htmlText = html
      ? (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').trim()
      : '';

    const hasRealText = html ? htmlText.length > 0 : plain.trim().length > 0;
    if (hasRealText) return;

    if (promptToolsConfig?.attachFiles === false) {
      event.preventDefault();
      notify({
        message: 'This app does not support file uploads or adding sources.',
        type: 'error',
        toastId: 'FILE_PASTE_NOT_ALLOWED_ID',
      });
      return;
    }

    const allowedDocTypes = [...ALLOWED_TYPES];

    const pastedImages: File[] = [];
    const pastedDocs: File[] = [];
    const unsupported: string[] = [];
    const existingNames = new Set(attachedFileArray.map((f) => f.name));

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file') continue;

      const file = item.getAsFile();
      if (!file) continue;

      if (file.type.startsWith('image/')) {
        const uniqueName = getUniquePastedFileName(file, existingNames);
        existingNames.add(uniqueName);
        const renamedFile =
          uniqueName === file.name ? file : new File([file], uniqueName, { type: file.type });
        pastedImages.push(renamedFile);
        continue;
      }

      if (allowedDocTypes.includes(file.type)) {
        pastedDocs.push(file);
        continue;
      }

      unsupported.push(file.name || file.type || 'unknown file');
    }

    if (pastedImages.length === 0 && pastedDocs.length === 0 && unsupported.length === 0) return;

    const totalPastedFiles = pastedImages.length + pastedDocs.length;
    if (totalPastedFiles > MAX_FILE_COUNT) {
      event.preventDefault();
      notify({
        message: `You can only paste up to ${MAX_FILE_COUNT} files at a time.`,
        type: 'error',
      });
      return;
    }

    event.preventDefault();

    if (unsupported.length > 0) {
      notify({
        message: `These files are not supported: ${unsupported.join(', ')}.`,
        type: 'error',
        toastId: FILE_PASTE_UNSUPPORTED_ID,
      });
    }

    setShowFileToasts?.(true);

    const uploadPromises: Promise<void>[] = [];

    if (pastedImages.length > 0) {
      uploadPromises.push(validateAndUploadFiles(pastedImages, IMAGE_ALLOWED_TYPES));
    }

    if (pastedDocs.length > 0) {
      uploadPromises.push(validateAndUploadFiles(pastedDocs, allowedDocTypes));
    }

    await Promise.all(uploadPromises);
  };

  const handleAttachFileClick = () => {
    if (chatAgent === 'app-agent') {
      setBrandDoc(true);
    }
    fileInputRef.current?.click();
  };

  const decrementCategoryCount = (fileNames: string[]) => {
    setCategoryCount?.((prev) => {
      const updated = { ...prev };

      fileNames.forEach((name) => {
        const category = chatInputSourceCategories?.[name];

        if (!category) return;

        updated[category] = Math.max(0, (updated[category] ?? 0) - 1);
      });

      return updated;
    });

    setChatInputSourceCategories?.((prev) => {
      const updated = { ...prev };

      fileNames.forEach((name) => {
        delete updated[name];
      });

      return updated;
    });

    setChatInputSourceTitles?.((prev) => {
      const updated = { ...prev };
      fileNames.forEach((name) => delete updated[name]);
      return updated;
    });
  };

  const handleRemoveFile = (fileName: string) => {
    const updatedFiles = attachedFileArray.filter((file) => file.name !== fileName);

    const updatedSelectedSources =
      selectedSources?.filter((source: string) => source !== fileName) ?? [];

    const updatedChatInputSources =
      chatInputSources?.filter((source: string) => source !== fileName) ?? [];

    const updatedSources = sources?.filter((source: string) => source !== fileName) ?? [];

    decrementCategoryCount([fileName]);

    setAttachedFileArray?.(updatedFiles);
    setSelectedSources?.(updatedSelectedSources);
    setSources?.(updatedSources);
    setChatInputSources?.(updatedChatInputSources);

    const remainingAttachedNames = new Set(updatedFiles.map((file) => file.name));

    const remainingDisplaySources = [
      ...updatedFiles.map((file) => file.name),
      ...updatedSelectedSources.filter((source) => !remainingAttachedNames.has(source)),
    ];

    if (remainingDisplaySources.length === 0) {
      setUploadedFile?.(null);
      setUploadedFileTitle?.('');
      setBrandDoc(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (chatAgent === 'app-agent') {
        setBrandDoc(false);
      } else {
        setChatAgent?.('Generic');
      }
    }
  };

  const removeFilesFromChatInput = (names: string[]) => {
    if (names.length === 0) return;

    const nameSet = new Set(names);

    setAttachedFileArray?.(attachedFileArray.filter((file) => !nameSet.has(file.name)));

    setChatInputSources?.((chatInputSources ?? []).filter((name) => !nameSet.has(name)));

    if (uploadedFileTitle && nameSet.has(uploadedFileTitle)) {
      setUploadedFile?.(null);
      setUploadedFileTitle?.('');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    fileInputRef,
    displaySources,
    isAnyFileUploading,
    handleFileSelect,
    handleAttachFileClick,
    handleRemoveFile,
    handlePaste,
    removeFilesFromChatInput,
  };
};
